import { Link, useParams, Navigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check } from "lucide-react";
import { getLandscapeProduct, landscapeProducts } from "@/lib/landscapeCatalog";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const LandscapeProduct = () => {
  const { slug } = useParams();
  const product = getLandscapeProduct(slug);

  if (!product) return <Navigate to="/robots/landscape" replace />;

  const related = landscapeProducts
    .filter((p) => p.category === product.category && p.slug !== product.slug)
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${product.name} | Neil AI Landscape Robotics`}
        description={product.summary.slice(0, 155)}
        keywords={`${product.name}, robotic mower, autonomous turf care, ${product.category.toLowerCase()}`}
      />
      <Navigation />

      <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <Link
            to="/robots/landscape"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Landscape Robotics
          </Link>

          <div className="mt-6 grid gap-8 lg:grid-cols-2">
            <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden">
              <img
                src={product.image}
                alt={`${product.name} autonomous landscape robot`}
                className="w-full aspect-[16/10] object-cover"
              />
            </Card>

            <div>
              <Badge variant="outline">{product.category}</Badge>
              <h1 className="mt-3 font-orbitron text-3xl md:text-4xl font-bold">{product.name}</h1>
              <p className="mt-2 text-primary">{product.tagline}</p>
              <p className="mt-4 text-muted-foreground">{product.summary}</p>

              <ul className="mt-6 space-y-2">
                {product.highlights.map((h) => (
                  <li key={h} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-wrap gap-2">
                {product.bestFor.map((b) => (
                  <span
                    key={b}
                    className="rounded-full border border-[hsl(var(--line))] px-3 py-1 text-xs text-muted-foreground"
                  >
                    {b}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    Get a Quote
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <a href="tel:3369624539">Call (336) 962-4539</a>
                </Button>
              </div>
            </div>
          </div>

          <section className="mt-14">
            <h2 className="font-orbitron text-2xl font-bold">Specifications</h2>
            <Card className="mt-4 glass-effect border-[hsl(var(--line))] overflow-hidden">
              <dl className="divide-y divide-[hsl(var(--line))]">
                {product.specs.map((s) => (
                  <div key={s.label} className="grid gap-1 p-4 sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm text-muted-foreground">{s.label}</dt>
                    <dd className="sm:col-span-2 text-sm font-medium">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
            <p className="mt-4 text-xs text-muted-foreground">
              Manufacturer-published specifications. Actual performance varies by turf conditions,
              terrain, and configuration.
            </p>
          </section>

          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="font-orbitron text-2xl font-bold">Related Models</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-3">
                {related.map((r) => (
                  <Card key={r.slug} className="glass-effect border-[hsl(var(--line))] overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.name}
                      className="h-36 w-full object-cover"
                      loading="lazy"
                    />
                    <div className="p-4">
                      <h3 className="text-sm font-semibold">{r.name}</h3>
                      <Button asChild size="sm" variant="outline" className="mt-3 rounded-full w-full">
                        <Link to={`/robots/landscape/${r.slug}`}>View Specs</Link>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default LandscapeProduct;
